import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Checkbox from 'components/inputs/checkbox';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import { ChatbotSubAgentType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

const EMPTY_SUB_AGENT: Omit<ChatbotSubAgentType, 'id'> = {
  code: '',
  name: '',
  description: '',
  whenToUse: '',
  systemPrompt: '',
  modelTaskCode: null,
  categoryId: 0,
  isActive: true,
  failedToolCallThreshold: null
};

export default function ChatbotSubAgentsPage() {
  const dispatch = useAppDispatch();
  const {
    chatbotSubAgents,
    chatbotConversationCategories,
    chatbotContextOptions,
    orchestratorTaskModels
  } = useAppSelector((state) => state.admin);

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [whenToUse, setWhenToUse] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [modelTaskCode, setModelTaskCode] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [failedToolCallThreshold, setFailedToolCallThreshold] =
    useState<string>('');
  const [contextOptionIds, setContextOptionIds] = useState<number[]>([]);

  const selectedAgent =
    chatbotSubAgents.find((a) => a.id === selectedId) ?? null;

  const filteredAgents = useMemo(() => {
    let agents = chatbotSubAgents;
    if (categoryFilter) {
      agents = agents.filter((a) => a.categoryId === Number(categoryFilter));
    }
    if (activeFilter === 'active') {
      agents = agents.filter((a) => a.isActive);
    } else if (activeFilter === 'inactive') {
      agents = agents.filter((a) => !a.isActive);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      agents = agents.filter(
        (a) =>
          a.code.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q)
      );
    }
    return agents;
  }, [chatbotSubAgents, categoryFilter, activeFilter, searchQuery]);

  const loadAgent = (agent: ChatbotSubAgentType | null) => {
    if (agent) {
      setCode(agent.code);
      setName(agent.name);
      setDescription(agent.description);
      setWhenToUse(agent.whenToUse);
      setSystemPrompt(agent.systemPrompt);
      setModelTaskCode(agent.modelTaskCode);
      setCategoryId(agent.categoryId);
      setIsActive(agent.isActive);
      setFailedToolCallThreshold(
        agent.failedToolCallThreshold !== null &&
          agent.failedToolCallThreshold !== undefined
          ? agent.failedToolCallThreshold.toString()
          : ''
      );
    } else {
      setCode(EMPTY_SUB_AGENT.code);
      setName(EMPTY_SUB_AGENT.name);
      setDescription(EMPTY_SUB_AGENT.description);
      setWhenToUse(EMPTY_SUB_AGENT.whenToUse);
      setSystemPrompt(EMPTY_SUB_AGENT.systemPrompt);
      setModelTaskCode(EMPTY_SUB_AGENT.modelTaskCode);
      setCategoryId(chatbotConversationCategories[0]?.id ?? 0);
      setIsActive(EMPTY_SUB_AGENT.isActive);
      setFailedToolCallThreshold('');
    }
  };

  useEffect(() => {
    if (isCreating) {
      loadAgent(null);
    } else {
      loadAgent(selectedAgent);
    }
  }, [selectedId, isCreating, chatbotSubAgents]);

  const fetchContextLinks = async (id: number) => {
    try {
      const res = await axios.get(`chatbot-sub-agent/${id}/context-links`);
      setContextOptionIds(res.data as number[]);
    } catch {
      setContextOptionIds([]);
    }
  };

  const handleSelect = (id: number) => {
    setIsCreating(false);
    setSelectedId(id);
    setView('detail');
    fetchContextLinks(id);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsCreating(true);
    loadAgent(null);
    setContextOptionIds([]);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (
      !code.trim() ||
      !name.trim() ||
      !description.trim() ||
      !whenToUse.trim() ||
      !systemPrompt.trim() ||
      !categoryId
    ) {
      errorModal(
        'Code, name, description, when to use, system prompt, and category are required.'
      );
      return;
    }

    let parsedThreshold: number | null = null;
    const trimmedThreshold = failedToolCallThreshold.trim();
    if (trimmedThreshold !== '') {
      const n = Number(trimmedThreshold);
      if (!Number.isInteger(n) || n < 1) {
        errorModal(
          'Failed tool-call threshold must be a positive integer (leave blank for default).'
        );
        return;
      }
      parsedThreshold = n;
    }

    const payload = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim(),
      whenToUse: whenToUse.trim(),
      systemPrompt: systemPrompt.trim(),
      modelTaskCode: modelTaskCode || null,
      categoryId,
      isActive,
      failedToolCallThreshold: parsedThreshold
    };

    try {
      if (isCreating) {
        const res = await axios.post('chatbot-sub-agent', payload);
        const newId = res.data?.id;
        if (newId) {
          await axios.put(`chatbot-sub-agent/${newId}/context-links`, {
            contextOptionIds
          });
        }
        dispatch(fetchDefaultAdminData());
        successModal('Sub-agent created.');
      } else if (selectedId) {
        await axios.patch('chatbot-sub-agent', { id: selectedId, ...payload });
        await axios.put(`chatbot-sub-agent/${selectedId}/context-links`, {
          contextOptionIds
        });
        dispatch(fetchDefaultAdminData());
        successModal('Sub-agent updated.');
      }
      setIsCreating(false);
      setSelectedId(null);
      setView('table');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        if (isCreating) loadAgent(null);
        else loadAgent(selectedAgent);
      }
    );
  };

  const handleDelete = () => {
    if (!selectedId) return;
    confirmationModal(
      `Are you sure you want to delete "${selectedAgent?.name}"? This will also delete all associated tools. This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`chatbot-sub-agent/${selectedId}`);
          setSelectedId(null);
          setView('table');
          dispatch(fetchDefaultAdminData());
          successModal('Sub-agent deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const getCategoryLabel = (cid: number) =>
    chatbotConversationCategories.find((c) => c.id === cid)?.label ?? '—';

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Sub-Agents
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {isCreating
            ? 'New Sub-Agent'
            : selectedAgent?.name ?? 'Edit Sub-Agent'}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Code</label>
            <Text
              value={code}
              onChange={(v) => setCode(v)}
              placeholder='e.g. system-design-agent'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Name</label>
            <Text
              value={name}
              onChange={(v) => setName(v)}
              placeholder='Display name'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Category
            </label>
            <Select
              value={categoryId}
              onChange={(v) => setCategoryId(Number(v))}
              options={chatbotConversationCategories.map((c) => ({
                label: c.label,
                value: c.id
              }))}
              preventDeselection
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Task Model Code
            </label>
            <Select
              value={modelTaskCode ?? ''}
              onChange={(v) => setModelTaskCode(v ? v.toString() : null)}
              options={[
                { label: '(None)', value: '' },
                ...orchestratorTaskModels.map((t) => ({
                  label: t.label,
                  value: t.code
                }))
              ]}
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Failed Tool-Call Threshold
            </label>
            <Text
              value={failedToolCallThreshold}
              onChange={(v) => setFailedToolCallThreshold(v)}
              placeholder='Blank for default'
            />
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <label className='relative inline-flex cursor-pointer items-center'>
            <input
              type='checkbox'
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className='peer sr-only'
            />
            <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
          <span className='text-sm font-semibold text-gray-700'>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            Description
          </label>
          <Textarea
            value={description}
            onChange={(v) => setDescription(v)}
            placeholder='What this sub-agent does'
            rows={3}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            When to Use
          </label>
          <Textarea
            value={whenToUse}
            onChange={(v) => setWhenToUse(v)}
            placeholder='Conditions for using this sub-agent'
            rows={3}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            System Prompt
          </label>
          <Textarea
            value={systemPrompt}
            onChange={(v) => setSystemPrompt(v)}
            placeholder='System prompt for the sub-agent'
            rows={10}
          />
        </div>

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-semibold text-gray-700'>
            Auto Provide Context
          </label>
          <p className='text-xs text-gray-500'>
            Select which context sections are provided to this sub-agent when it
            is delegated a task.
          </p>
          <div className='flex flex-col gap-1.5'>
            {chatbotContextOptions.map((option) => (
              <div key={option.id} className='flex items-center gap-2'>
                <Checkbox
                  value={contextOptionIds.includes(option.id)}
                  onChange={(checked) => {
                    if (checked) {
                      setContextOptionIds((prev) => [...prev, option.id]);
                    } else {
                      setContextOptionIds((prev) =>
                        prev.filter((id) => id !== option.id)
                      );
                    }
                  }}
                />
                <span className='text-sm text-gray-700'>{option.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button
            text={isCreating ? 'Create Sub-Agent' : 'Save Changes'}
            color='green'
            onClick={handleSave}
            disabled={
              !code.trim() ||
              !name.trim() ||
              !description.trim() ||
              !systemPrompt.trim()
            }
          />
          <Button text='Reset' color='white' onClick={handleReset} />
          {!isCreating && selectedId && (
            <div className='ml-auto'>
              <Button
                text='Delete Sub-Agent'
                color='red'
                onClick={handleDelete}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Chatbot Sub-Agents</div>

      <div className='flex items-end gap-3'>
        <div className='w-72'>
          <Text
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder='Search sub-agents...'
            controlled
          />
        </div>
        <div className='w-56'>
          <Select
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v.toString())}
            options={[
              { label: 'All Categories', value: '' },
              ...chatbotConversationCategories.map((c) => ({
                label: c.label,
                value: c.id
              }))
            ]}
          />
        </div>
        <div className='w-40'>
          <Select
            value={activeFilter}
            onChange={(v) => setActiveFilter(v.toString())}
            options={[
              { label: 'All Status', value: '' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' }
            ]}
          />
        </div>
        <div className='ml-auto'>
          <Button
            text='New Sub-Agent'
            color='green'
            onClick={handleNew}
            icon={PlusIcon}
          />
        </div>
      </div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {['Code', 'Name', 'Category', 'Task Model', 'Active'].map(
                (header) => (
                  <th
                    key={header}
                    scope='col'
                    className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredAgents.map((agent) => (
              <tr
                key={agent.id}
                onClick={() => handleSelect(agent.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  {agent.code}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {agent.name}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {getCategoryLabel(agent.categoryId)}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {agent.modelTaskCode ?? '—'}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm'>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      agent.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
            {filteredAgents.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No sub-agents found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredAgents.length} sub-agent
        {filteredAgents.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
